import React, { useState } from 'react';
import { Form, Input, Button, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone, MailOutlined, LockOutlined } from '@ant-design/icons';
import { Shield, BarChart3, TrendingUp, Calculator, PieChart, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const { login } = useAuth();
  const navigate = useNavigate();

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      await login(values.email, values.password);
      message.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      message.error(error.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 lg:bg-white flex">
      {/* Desktop Left Side - Accounting Illustration */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="2" fill="white" opacity="0.3"/>
                <circle cx="40" cy="40" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="0" cy="0" r="1" fill="white" opacity="0.1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)"/>
          </svg>
        </div>

        {/* Cloud-like decorative elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-blue-400 opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white opacity-8 rounded-full blur-2xl"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center text-center px-12 w-full">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-3xl mb-6 shadow-2xl">
              <div className="text-3xl font-bold text-gray-800">$</div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Divya Enterprise</h1>
            <p className="text-xl text-gray-300 mb-8">Business Account Manager</p>
          </div>

          {/* Accounting Illustration */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-3">
                <Calculator className="w-8 h-8 text-white" />
              </div>
              <span className="text-white text-sm">Calculate</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-3">
                <PieChart className="w-8 h-8 text-white" />
              </div>
              <span className="text-white text-sm">Analyze</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-3">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <span className="text-white text-sm">Report</span>
            </div>
          </div>

          <div className="text-gray-300 text-lg max-w-md">
            Streamline your business accounting with powerful tools designed for modern enterprises.
          </div>
        </div>
      </div>

      {/* Right Side - Form (Desktop) / Full Screen (Mobile) */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 relative overflow-hidden">
          {/* Mobile Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="mobile-dots" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                  <circle cx="15" cy="15" r="1.5" fill="white" opacity="0.4"/>
                  <circle cx="30" cy="30" r="1" fill="white" opacity="0.2"/>
                  <circle cx="0" cy="0" r="0.8" fill="white" opacity="0.1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#mobile-dots)"/>
            </svg>
          </div>

          {/* Mobile decorative elements */}
          <div className="absolute top-5 right-5 w-20 h-20 bg-white opacity-5 rounded-full blur-2xl"></div>
          <div className="absolute bottom-5 left-5 w-24 h-24 bg-blue-400 opacity-8 rounded-full blur-2xl"></div>

          <div className="relative z-10 pt-12 pb-8 text-center px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 shadow-xl">
              <div className="text-2xl font-bold text-gray-800">$</div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Divya Enterprise</h1>
            <p className="text-gray-300 text-lg">Business Account Manager</p>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 bg-white lg:bg-gray-50 relative">
          {/* Mobile curved overlay effect */}
          <div className="lg:hidden absolute top-0 left-0 right-0 h-8 bg-white rounded-t-3xl -mt-6 relative z-10"></div>
          
          <div className="lg:flex lg:items-center lg:justify-center lg:min-h-screen lg:px-12">
            <div className="w-full max-w-md lg:max-w-lg bg-white lg:rounded-2xl lg:shadow-2xl lg:border lg:border-gray-200 p-8 lg:p-12 relative z-20">
              {/* Desktop Header */}
              <div className="hidden lg:block text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 rounded-2xl mb-4">
                  <div className="text-2xl font-bold text-white">$</div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h2>
                <p className="text-gray-600">Sign in to continue to your account</p>
              </div>

              {/* Mobile Header */}
              <div className="lg:hidden mb-8 pt-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h2>
                <p className="text-gray-600">Sign in to continue to your account</p>
              </div>

              {/* Login Form */}
              <Form
                form={form}
                name="login"
                onFinish={onFinish}
                layout="vertical"
                size="large"
                className="login-form"
              >
                {/* Email Field */}
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Invalid email address' }
                  ]}
                  className="mb-6"
                >
                  <Input
                    prefix={<MailOutlined className="text-gray-400 mr-2" />}
                    placeholder="Email Address"
                    className="custom-input"
                  />
                </Form.Item>

                {/* Password Field */}
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: 'Password is required' },
                    { min: 6, message: 'Password must be at least 6 characters' }
                  ]}
                  className="mb-4"
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400 mr-2" />}
                    placeholder="Password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    className="custom-input"
                  />
                </Form.Item>

                {/* Forgot Password */}
                <div className="text-right mb-6">
                  <Link 
                    to="/forgot-password" 
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    Forgot Password?
                  </Link>
                </div>

                {/* Login Button */}
                <Form.Item className="mb-6">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    className="custom-button w-full"
                  >
                    {!isLoading && (
                      <>
                        Login
                      </>
                    )}
                  </Button>
                </Form.Item>
              </Form>

              {/* Register Link */}
              <div className="text-center mb-8">
                <span className="text-gray-600">New to Divya Enterprise? </span>
                <Link to="/register" className="text-gray-800 font-semibold hover:text-gray-900">
                  Create Account
                </Link>
              </div>

              {/* Features Row */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <Shield className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Secure</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <BarChart3 className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Analytics</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <TrendingUp className="w-5 h-5 text-gray-600" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Growth</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 py-4 text-center">
          <p className="text-gray-500 text-sm">© 2025 Divya Enterprise. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;